import { supabase, callProxy } from './supabase';

export interface Translation {
    id?: string;
    key: string;
    vi: string;
    en: string;
    group_name: string;
    description?: string;
}

const CACHE_KEY = 'app_translations';
const CACHE_VERSION_KEY = 'app_translations_version';

export async function fetchTranslations(): Promise<Translation[]> {
    const { data, error } = await supabase
        .from('system_translations')
        .select('*')
        .order('key');

    if (error) {
        console.error('Error fetching translations:', error);
        return [];
    }

    return data || [];
}

export async function fetchUniqueGroups(): Promise<string[]> {
    const { data, error } = await supabase
        .from('system_translations')
        .select('group_name');

    if (error) {
        console.error('Error fetching unique groups:', error);
        return [];
    }

    const groups = [...new Set(data.map(item => item.group_name).filter(Boolean))];
    return groups.sort();
}

export async function getLocalTranslations() {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
        return JSON.parse(cached);
    }
    return null;
}

export function saveTranslationsToLocal(translations: Translation[]) {
    const flatVi: Record<string, string> = {};
    const flatEn: Record<string, string> = {};

    translations.forEach((t) => {
        flatVi[t.key] = t.vi;
        flatEn[t.key] = t.en;
    });

    localStorage.setItem(CACHE_KEY, JSON.stringify({ vi: flatVi, en: flatEn }));
    localStorage.setItem(CACHE_VERSION_KEY, Date.now().toString());
}

/**
 * Sync translations with Supabase and return them
 */
export async function syncTranslations() {
    const translations = await fetchTranslations();
    if (translations.length > 0) {
        saveTranslationsToLocal(translations);
    }
    return translations;
}

export async function createTranslation(data: Partial<Translation>) {
    await callProxy({
        action: 'upsert',
        table: 'system_translations',
        onConflict: 'key',
        data: data
    });

    // Refresh cache
    await syncTranslations();
}

export async function updateTranslation(key: string, data: Partial<Translation>) {
    await callProxy({
        action: 'upsert',
        table: 'system_translations',
        onConflict: 'key',
        data: { ...data, key }
    });

    // Refresh cache
    await syncTranslations();
}

/**
 * Upsert multiple translations (used for Import)
 */
export async function upsertTranslations(items: Partial<Translation>[]) {
    await callProxy({
        action: 'upsert',
        table: 'system_translations',
        onConflict: 'key',
        data: items
    });

    // Refresh cache
    await syncTranslations();
}
export async function deleteTranslation(key: string) {
    await callProxy({
        action: 'delete',
        table: 'system_translations',
        eqField: 'key',
        eqValue: key
    });

    // Refresh cache
    await syncTranslations();
}
